import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Save, FolderOpen, FilePlus, Trash2, BarChart3, Printer, HelpCircle, Copy, Clock } from 'lucide-react';
import { toneShiftApi } from '../services/toneShiftApi';
import { useDocumentEditor } from '../hooks/useDocumentEditor';
import DocumentPickerModal from '../components/DocumentPickerModal';
import Selector from '../components/Selector';
import EditModal from '../components/modals/EditModal';
import { getModifierLevelDisplayName, getModifierLevelName as getModifierLevelNameFromMapping } from '../constants/toneShiftMappings';
import { buildToneShiftPrompt } from '../utils/promptBuilder';
import { activeModifierLevels } from '../utils/selectionUtils';
import { useModelSelector } from '../hooks/useModelSelector';
import { toast } from 'react-toastify';
import llmService from '../services/llmService';
import '../styles/DocumentEditor.css';
import '../styles/BaseContentForm.css';

const DocumentEditor = () => {
  // Document management hook
  const {
    currentDocument,
    hasUnsavedChanges,
    isSaving,
    createNewDocument,
    updateEditorContent,
    updateTitle,
    saveDocument,
    loadDocument,
    deleteDocument,
    canCreateNew,
    canOpenDocument
  } = useDocumentEditor();

  // UI state
  const [wordCountVisible, setWordCountVisible] = useState(false);
  const [helpPanelVisible, setHelpPanelVisible] = useState(false);
  const [wordCount, setWordCount] = useState({ words: 0, characters: 0 });
  const [lineSpacing, setLineSpacing] = useState('1.5');
  const [showDocumentPicker, setShowDocumentPicker] = useState(false);
  
  // Function level state
  const [activeFunction, setActiveFunction] = useState('manage-file'); // Default to manage file
  const [showFunctionToolbar, setShowFunctionToolbar] = useState(true);
  
  // Transform state
  const [toneShiftData, setToneShiftData] = useState({
    voices: [],
    roles: [],
    formats: [],
    audiences: [],
    modifiers: []
  });
  const [selectedVoice, setSelectedVoice] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('');
  const [selectedAudiences, setSelectedAudiences] = useState([]);
  const [modifierLevels, setModifierLevels] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Transform generation state
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [generatedResponse, setGeneratedResponse] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [conversationMessages, setConversationMessages] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showChatEdit, setShowChatEdit] = useState(false);
  const [chatEditInput, setChatEditInput] = useState('');
  
  // Model selection
  const { availableModels, selectedModel, select: setSelectedModel, loading: modelsLoading } = useModelSelector();
  
  // Analysis state
  const [analyses, setAnalyses] = useState([]);
  const [analysisPrompt, setAnalysisPrompt] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisResponse, setAnalysisResponse] = useState('');
  const [analysisHistory, setAnalysisHistory] = useState([]);
  
  // Summarize state
  const [summaryPrompt, setSummaryPrompt] = useState('');
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryResponse, setSummaryResponse] = useState('');
  const [summaryHistory, setSummaryHistory] = useState([]);
  
  // History dropdown state
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const historyDropdownRef = useRef(null);
  
  // Refs
  const editorRef = useRef(null);
  const helpTimeoutRef = useRef(null);
  const selectionTimeoutRef = useRef(null);

  // Format document function
  const formatDoc = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
    }
  }, []);

  // Update word count
  const updateWordCount = useCallback(() => {
    if (!editorRef.current) return;
    
    const text = editorRef.current.innerText || editorRef.current.textContent || '';
    const words = text.trim().split(/\s+/).filter(word => word.length > 0).length;
    const chars = text.length;
    
    setWordCount({ words, characters: chars });
  }, []);

  // Track text selection
  const handleSelectionChange = useCallback(() => {
    // Debounce selection tracking
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }
    
    selectionTimeoutRef.current = setTimeout(() => {
      const selection = window.getSelection();
      if (!selection.rangeCount || !editorRef.current?.contains(selection.anchorNode)) {
        setSelectedText('');
        setSelectionRange(null);
        return;
      }

      const range = selection.getRangeAt(0);
      const selectedTextContent = range.toString();
      
      setSelectedText(selectedTextContent);
      setSelectionRange(range.cloneRange());
    }, 100);
  }, []);

  // Get selection stats
  const getSelectionStats = useCallback(() => {
    if (!editorRef.current) return { selected: 0, total: 0 };
    
    const totalText = editorRef.current.innerText || editorRef.current.textContent || '';
    const totalChars = totalText.length;
    // If nothing selected, treat as entire document selected
    const selectedChars = selectedText.length || totalChars;
    
    return { selected: selectedChars, total: totalChars };
  }, [selectedText]);

  // Handle editor input
  const handleEditorInput = useCallback(() => {
    updateWordCount();
    if (editorRef.current) {
      updateEditorContent(editorRef.current.innerHTML);
    }
  }, [updateWordCount, updateEditorContent]);

  // Handle paste
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain');
    document.execCommand('insertHTML', false, text);
  }, []);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      switch(e.key) {
        case 'b':
          e.preventDefault();
          formatDoc('bold');
          break;
        case 'i':
          e.preventDefault();
          formatDoc('italic');
          break;
        case 'u':
          e.preventDefault();
          formatDoc('underline');
          break;
        case 's':
          e.preventDefault();
          saveDocument();
          break;
        case 'p':
          e.preventDefault();
          window.print();
          break;
        case 'y':
          e.preventDefault();
          document.execCommand('redo');
          break;
        case 'z':
          if (!e.shiftKey) {
            e.preventDefault();
            document.execCommand('undo');
          }
          break;
        default:
          break;
      }
    }
  }, [formatDoc]);

  // Toggle word count
  const toggleWordCount = useCallback(() => {
    setWordCountVisible(prev => {
      if (!prev) {
        // Show temporarily if not permanently visible
        setTimeout(() => {
          setWordCountVisible(false);
        }, 3000);
      }
      return !prev;
    });
  }, []);

  // Toggle help panel
  const toggleHelp = useCallback(() => {
    setHelpPanelVisible(true);
    clearTimeout(helpTimeoutRef.current);
    helpTimeoutRef.current = setTimeout(() => {
      setHelpPanelVisible(false);
    }, 10000);
  }, []);

  // Change line spacing
  const changeLineSpacing = useCallback((value) => {
    setLineSpacing(value);
    if (editorRef.current) {
      editorRef.current.style.lineHeight = value;
    }
  }, []);

  // Fetch ToneShift data when Transform is selected
  const fetchToneShiftData = useCallback(async () => {
    if (loading || toneShiftData.voices.length > 0) return; // Don't refetch if already loaded
    
    setLoading(true);
    try {
      const [voices, roles, formats, audiences, modifiers] = await Promise.all([
        toneShiftApi.getVoices(),
        toneShiftApi.getRoles(),
        toneShiftApi.getFormats(),
        toneShiftApi.getAudiences(),
        toneShiftApi.getModifiers()
      ]);
      
      setToneShiftData({ voices, roles, formats, audiences, modifiers });
    } catch (error) {
      console.error('Error fetching ToneShift data:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, toneShiftData.voices.length]);

  // Fetch Analyses when Analyze is selected
  const fetchAnalyses = useCallback(async () => {
    if (analyses.length > 0) return; // Don't refetch if already loaded
    
    try {
      const analysesData = await toneShiftApi.getAnalyses();
      setAnalyses(analysesData);
      
      // Set default analysis prompt to "Text Analysis" if available
      const defaultAnalysis = analysesData.find(a => a.name === 'Text Analysis');
      if (defaultAnalysis && !analysisPrompt) {
        setAnalysisPrompt(defaultAnalysis.updated_prompt || defaultAnalysis.starter_prompt || '');
      }
    } catch (error) {
      console.error('Error fetching analyses:', error);
      toast.error('Failed to load analysis prompts');
    }
  }, [analyses.length, analysisPrompt]);

  // Fetch Summary prompt
  const fetchSummaryPrompt = useCallback(async () => {
    if (summaryPrompt) return; // Don't refetch if already loaded
    
    try {
      const summariesData = await toneShiftApi.getSummaries();
      const defaultSummary = summariesData.find(s => s.name === 'Summarize Text');
      if (defaultSummary && !summaryPrompt) {
        setSummaryPrompt(defaultSummary.updated_prompt || defaultSummary.starter_prompt || '');
      }
    } catch (error) {
      console.error('Error fetching summary prompt:', error);
      toast.error('Failed to load summary prompts');
    }
  }, [summaryPrompt]);

  // Initialize with a new document on mount
  useEffect(() => {
    if (!currentDocument) {
      createNewDocument();
    }
  }, [currentDocument, createNewDocument]);
  
  // Fetch ToneShift data when Transform function is activated
  useEffect(() => {
    if (activeFunction === 'transform') {
      fetchToneShiftData();
    }
  }, [activeFunction, fetchToneShiftData]);

  // Fetch Analyses when Analyze function is activated
  useEffect(() => {
    if (activeFunction === 'analyze') {
      fetchAnalyses();
      fetchSummaryPrompt();
    }
  }, [activeFunction, fetchAnalyses, fetchSummaryPrompt]);

  // Track previous document ID to avoid re-focusing on title changes
  const prevDocIdRef = useRef(null);

  // Load document content into editor when document changes
  useEffect(() => {
    if (editorRef.current && currentDocument) {
      // Only reload content and focus if it's a different document
      if (prevDocIdRef.current !== currentDocument.id) {
        editorRef.current.innerHTML = currentDocument.content || '';
        updateWordCount();
        editorRef.current.focus();
        prevDocIdRef.current = currentDocument.id;
      }
    }
  }, [currentDocument, updateWordCount]);

  // Track selection changes
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, [handleSelectionChange]);

  // Close history dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (historyDropdownRef.current && !historyDropdownRef.current.contains(event.target)) {
        setShowHistoryDropdown(false);
      }
    };

    if (showHistoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showHistoryDropdown]);

  // Warn user about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Update document title
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    updateTitle(newTitle);
    document.title = newTitle + ' - Canvas';
  };

  // Handle new document
  const handleNewDocument = () => {
    if (canCreateNew()) {
      createNewDocument();
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
    }
  };

  // Handle open document
  const handleOpenDocument = () => {
    if (canOpenDocument()) {
      setShowDocumentPicker(true);
    }
  };

  // Handle document selection from picker
  const handleSelectDocument = (doc) => {
    const content = loadDocument(doc);
    if (editorRef.current) {
      editorRef.current.innerHTML = content;
    }
    updateWordCount();
  };

  // Handle delete document
  const handleDeleteDocument = async () => {
    if (!currentDocument?.id) {
      return;
    }
    
    if (window.confirm(`Delete "${currentDocument.title}"? This cannot be undone.`)) {
      const success = await deleteDocument();
      if (success && editorRef.current) {
        editorRef.current.innerHTML = '';
      }
    }
  };

  // Check command states for active buttons
  const isCommandActive = (command) => {
    return document.queryCommandState(command);
  };
  
  // Modifier helper functions
  const getModifierLabels = (modifier) => {
    // Use the left and right properties from the transformed modifier data
    if (modifier.left && modifier.right) {
      return { left: modifier.left, right: modifier.right };
    }
    
    if (!modifier.levels || modifier.levels.length === 0) {
      return { left: 'Low', right: 'High' };
    }
    
    // Create a mapping of modifier names to their proper labels
    const labelMappings = {
      'length': { left: 'Most Concise', right: 'Most Detailed' },
      'assertiveness': { left: 'Most Collaborative', right: 'Most Decisive' },
      'directness': { left: 'Most Diplomatic', right: 'Most Blunt' },
      'energy': { left: 'Most Calm', right: 'Most Excited' },
      'formality': { left: 'Most Casual', right: 'Most Professional' },
      'urgency': { left: 'Most Relaxed', right: 'Most Immediate' },
      'humor': { left: 'Most Humorous', right: 'Most Serious' }
    };
    
    // If we have a specific mapping for this modifier, use it
    const modifierNameLower = modifier.name.toLowerCase();
    if (labelMappings[modifierNameLower]) {
      return labelMappings[modifierNameLower];
    }
    
    // Otherwise, try to extract from level names
    const sortedLevels = [...modifier.levels].sort((a, b) => {
      const levelOrder = {
        'most_collaborative': 1, 'more_collaborative': 2, 'collaborative': 3,
        'decisive': 4, 'more_decisive': 5, 'most_decisive': 6,
        'most_concise': 1, 'more_concise': 2, 'concise': 3,
        'detailed': 4, 'more_detailed': 5, 'most_detailed': 6,
        'most_professional': 1, 'more_professional': 2, 'professional': 3,
        'personal': 4, 'more_personal': 5, 'most_personal': 6,
        'most_diplomatic': 1, 'more_diplomatic': 2, 'diplomatic': 3,
        'blunt': 4, 'more_blunt': 5, 'most_blunt': 6,
        'most_calm': 1, 'more_calm': 2, 'calm': 3,
        'excited': 4, 'more_excited': 5, 'most_excited': 6,
        'most_casual': 1, 'more_casual': 2, 'casual': 3,
        'professional': 4, 'more_professional': 5, 'most_professional': 6,
        'most_relaxed': 1, 'more_relaxed': 2, 'relaxed': 3,
        'immediate': 4, 'more_immediate': 5, 'most_immediate': 6,
        'most_humorous': 1, 'more_humorous': 2, 'humorous': 3,
        'serious': 4, 'more_serious': 5, 'most_serious': 6
      };
      return (levelOrder[a.level_name] || 0) - (levelOrder[b.level_name] || 0);
    });
    
    if (sortedLevels.length >= 2) {
      const leftLabel = sortedLevels[0].level_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const rightLabel = sortedLevels[sortedLevels.length - 1].level_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return { left: leftLabel, right: rightLabel };
    }
    
    return { left: 'Low', right: 'High' };
  };
  
  const getLevelDisplayName = (modifier, level) => {
    const displayName = getModifierLevelDisplayName(modifier.name, level);
    return displayName;
  };
  
  const adjustModifier = (modifierId, delta) => {
    setModifierLevels(prev => {
      const currentLevel = parseInt(prev[modifierId] || 0);
      let newLevel = currentLevel + delta;
      // Clamp to -3 to +3 range
      newLevel = Math.max(-3, Math.min(3, newLevel));
      return {
        ...prev,
        [modifierId]: newLevel
      };
    });
  };
  
  // Handle edit item
  const handleEditItem = (item) => {
    // Determine item type if not already set
    let itemType = item.type;
    if (!itemType) {
      // Check which array contains this item to determine type
      if (toneShiftData.voices.some(v => v.id === item.id)) {
        itemType = 'voice';
      } else if (toneShiftData.roles.some(r => r.id === item.id)) {
        itemType = 'role';
      } else if (toneShiftData.formats.some(f => f.id === item.id)) {
        itemType = 'format';
      } else if (toneShiftData.audiences.some(a => a.id === item.id)) {
        itemType = 'audience';
      } else if (toneShiftData.modifiers.some(m => m.id === item.id)) {
        itemType = 'modifier';
      } else if (analyses.some(a => a.id === item.id)) {
        itemType = 'analysis';
      }
    }
    
    // Set up the editing item with proper prompt handling
    let editingItem = {
      ...item,
      type: itemType,
      // Store the original prompt for reference
      originalPrompt: item.starter_prompt || item.prompt || ''
    };

    // Special handling for modifiers - ensure levels are properly set up
    if (itemType === 'modifier' && item.levels) {
      editingItem = {
        ...editingItem,
        // For modifiers, we don't use the main prompt field
        levels: item.levels.map(level => ({
          ...level,
          // Ensure each level has the proper prompt structure
          updated_prompt: level.updated_prompt || null,
          starter_prompt: level.starter_prompt || level.prompt || ''
        }))
      };
    } else {
      // For non-modifiers, use the regular prompt handling
      editingItem.prompt = item.updated_prompt || item.starter_prompt || item.prompt || '';
    }

    setEditingItem(editingItem);
    setShowEditModal(true);
  };
  
  // Handle generate transform
  const handleGenerate = async () => {
    // Get input text - use selected text if available, otherwise whole document
    const inputText = selectedText || (editorRef.current?.innerText || editorRef.current?.textContent || '');
    
    if (!inputText.trim()) {
      toast.error('Please select text or ensure the document has content');
      return;
    }

    // Check if we have any parameters selected
    const hasParams = selectedVoice || selectedRole || selectedFormat || 
                      selectedAudiences.length > 0 || 
                      Object.values(modifierLevels).some(l => parseInt(l) !== 0);
    
    if (!hasParams) {
      toast.error('Please select at least one transformation parameter');
      return;
    }

    setIsGenerating(true);
    setShowResponseModal(true);
    setGeneratedResponse('');

    try {
      const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
      
      // Build the prompt
      const { text: completePrompt } = buildToneShiftPrompt({
        inputText,
        messages: conversationMessages,
        voices: toneShiftData.voices,
        roles: toneShiftData.roles,
        formats: toneShiftData.formats,
        audiences: toneShiftData.audiences,
        modifiers: toneShiftData.modifiers,
        selectedVoice,
        selectedRole,
        selectedFormat,
        selectedAudiences,
        modifierLevelsMap: activeModifierLevels(modifierLevels),
        selectedModelInfo,
        getModifierLevelName: (name, level) => {
          const modifier = toneShiftData.modifiers.find(m => m.name === name);
          if (!modifier) return null;
          const levelName = getModifierLevelNameFromMapping(name, level);
          return levelName;
        },
        getLevelDisplayName
      });

      // Create or update conversation
      let conversationId = currentConversationId;
      if (!conversationId) {
        const conversationData = {
          title: inputText.slice(0, 50) + (inputText.length > 50 ? '...' : ''),
          original_content: inputText,
          voice_ids: selectedVoice ? [selectedVoice] : [],
          role_ids: selectedRole ? [selectedRole] : [],
          format_ids: selectedFormat ? [selectedFormat] : [],
          audience_ids: selectedAudiences,
          modifier_ids: [],
          modifier_levels: Object.fromEntries(
            Object.entries(modifierLevels)
              .filter(([_, level]) => parseInt(level) !== 0)
              .map(([key, level]) => [key, parseInt(level)])
          )
        };
        const newConversation = await toneShiftApi.createConversation(conversationData);
        conversationId = newConversation.id;
        setCurrentConversationId(conversationId);
      } else {
        // Update parameters
        await toneShiftApi.updateParameters(conversationId, {
          voice_ids: selectedVoice ? [selectedVoice] : [],
          role_ids: selectedRole ? [selectedRole] : [],
          format_ids: selectedFormat ? [selectedFormat] : [],
          audience_ids: selectedAudiences,
          modifier_ids: [],
          modifier_levels: Object.fromEntries(
            Object.entries(modifierLevels)
              .filter(([_, level]) => parseInt(level) !== 0)
              .map(([key, level]) => [key, parseInt(level)])
          )
        });
      }

      // Send message
      const modelInfo = selectedModelInfo ? { provider: selectedModelInfo.provider, model: selectedModelInfo.model } : null;
      const messageType = conversationMessages.length === 0 ? 'original_message' : 'additional_instructions';
      
      const messageResult = await toneShiftApi.sendMessage(conversationId, {
        content: completePrompt,
        message_type: messageType,
        provider: modelInfo ? modelInfo.provider : null,
        model: modelInfo ? modelInfo.model : null
      });

      if (messageResult.ai_response) {
        const responseText = messageResult.ai_response.content;
        const additionalContextText = messageResult.ai_response.metadata?.additional_context || '';
        setGeneratedResponse(responseText);
        setAdditionalContext(additionalContextText);
        
        // Add to conversation messages
        const userMessage = {
          id: Date.now(),
          type: 'input',
          text: inputText,
          timestamp: new Date().toLocaleTimeString()
        };
        const aiMessage = {
          id: Date.now() + 1,
          type: 'output',
          text: responseText,
          additionalContext: additionalContextText,
          timestamp: new Date().toLocaleTimeString()
        };
        setConversationMessages(prev => [...prev, userMessage, aiMessage]);
      } else {
        throw new Error(messageResult.error || 'Failed to get AI response');
      }
    } catch (error) {
      console.error('Error generating transform:', error);
      toast.error(error.message || 'Failed to generate transform');
      setShowResponseModal(false);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle commit - replace selected text with generated response
  const handleCommit = () => {
    if (!generatedResponse || !editorRef.current) return;

    try {
      // Focus the editor first
      editorRef.current.focus();
      
      if (selectionRange && selectedText) {
        // Try to restore the selection range
        const selection = window.getSelection();
        selection.removeAllRanges();
        
        try {
          // Try to use the stored range directly
          selection.addRange(selectionRange);
          
          // Verify the selection is valid and contains the expected text
          if (selection.toString() === selectedText || selection.rangeCount > 0) {
            // Use execCommand to replace selected text
            document.execCommand('insertText', false, generatedResponse);
          } else {
            throw new Error('Selection does not match expected text');
          }
        } catch (rangeError) {
          // Range is detached, try to find the text and recreate selection
          console.warn('Range detached, attempting to restore selection:', rangeError);
          
          // Find the selected text in the editor content
          const editorText = editorRef.current.innerText || editorRef.current.textContent || '';
          const textIndex = editorText.indexOf(selectedText);
          
          if (textIndex !== -1) {
            // Create a new range using text node walking
            const range = document.createRange();
            const walker = document.createTreeWalker(
              editorRef.current,
              NodeFilter.SHOW_TEXT,
              null
            );
            
            let charCount = 0;
            let startNode = null;
            let endNode = null;
            let startOffset = 0;
            let endOffset = 0;
            
            let node;
            while (node = walker.nextNode()) {
              const nodeLength = node.textContent.length;
              
              // Check if selection start is in this node
              if (!startNode && charCount + nodeLength >= textIndex) {
                startNode = node;
                startOffset = textIndex - charCount;
              }
              
              // Check if selection end is in this node
              if (startNode && charCount + nodeLength >= textIndex + selectedText.length) {
                endNode = node;
                endOffset = (textIndex + selectedText.length) - charCount;
                break;
              }
              
              charCount += nodeLength;
            }
            
            if (startNode && endNode) {
              range.setStart(startNode, startOffset);
              range.setEnd(endNode, endOffset);
              selection.removeAllRanges();
              selection.addRange(range);
              
              // Replace the selected text
              document.execCommand('insertText', false, generatedResponse);
            } else {
              throw new Error('Could not restore selection - text nodes not found');
            }
          } else {
            throw new Error('Selected text not found in editor');
          }
        }
      } else {
        // No selection - replace whole document
        editorRef.current.innerHTML = generatedResponse;
      }

      // Update editor content
      updateEditorContent(editorRef.current.innerHTML);
      updateWordCount();
      
      // Close modal and reset
      setShowResponseModal(false);
      setGeneratedResponse('');
      setAdditionalContext('');
      setSelectedText('');
      setSelectionRange(null);
      toast.success('Text replaced successfully');
    } catch (error) {
      console.error('Error committing text:', error);
      toast.error('Failed to replace text');
    }
  };

  // Handle discard
  const handleDiscard = () => {
    setShowResponseModal(false);
    setGeneratedResponse('');
    setAdditionalContext('');
    setSelectedText('');
    setSelectionRange(null);
  };

  // Handle analyze
  const handleAnalyze = async () => {
    // Get input text - use selected text if available, otherwise whole document
    const inputText = selectedText || (editorRef.current?.innerText || editorRef.current?.textContent || '');
    
    if (!inputText.trim()) {
      toast.error('Please select text or ensure the document has content');
      return;
    }

    if (!analysisPrompt.trim()) {
      toast.error('Analysis prompt is not available');
      return;
    }

    if (!selectedModel) {
      toast.error('Please select a model');
      return;
    }

    setIsAnalyzing(true);
    setShowAnalysisModal(true);
    setAnalysisResponse('');

    try {
      const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
      
      // Build the complete prompt by combining the analysis prompt with the text
      const completePrompt = `${analysisPrompt}\n\nText to analyze:\n${inputText}`;

      // Call LLM service
      const result = await llmService.generateText({
        prompt: completePrompt,
        provider: selectedModelInfo?.provider,
        parameters: selectedModelInfo?.model ? { 
          model: selectedModelInfo.model,
          ...(selectedModelInfo?.provider === 'OpenAI' && { temperature: 0.7, max_tokens: 2000 })
        } : {}
      });

      if (result.success && result.data) {
        let responseText = result.data.response_text || result.data.response || result.data.content || '';
        
        // Strip markdown code blocks if present
        responseText = responseText.trim();
        if (responseText.startsWith('```json')) {
          responseText = responseText.replace(/^```json\s*/i, '').replace(/```\s*$/, '');
        } else if (responseText.startsWith('```')) {
          responseText = responseText.replace(/^```\s*/, '').replace(/```\s*$/, '');
        }
        responseText = responseText.trim();
        
        // Try to parse JSON response
        try {
          const jsonResponse = JSON.parse(responseText);
          setAnalysisResponse(jsonResponse);
          // Add to history
          setAnalysisHistory(prev => [{
            id: Date.now(),
            timestamp: new Date().toISOString(),
            response: jsonResponse,
            text: inputText,
            type: 'analysis'
          }, ...prev]);
        } catch (e) {
          // If not JSON, store as raw text
          setAnalysisResponse(responseText);
          setAnalysisHistory(prev => [{
            id: Date.now(),
            timestamp: new Date().toISOString(),
            response: responseText,
            text: inputText,
            type: 'analysis'
          }, ...prev]);
        }
      } else {
        throw new Error(result.error || 'Failed to generate analysis');
      }
    } catch (error) {
      console.error('Error analyzing text:', error);
      toast.error(error.message || 'Failed to analyze text');
      setShowAnalysisModal(false);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handle analysis discard
  const handleAnalysisDiscard = () => {
    setShowAnalysisModal(false);
    setAnalysisResponse('');
  };

  const handleSummarize = async () => {
    // Get input text - use selected text if available, otherwise whole document
    const inputText = selectedText || (editorRef.current?.innerText || editorRef.current?.textContent || '');
    
    if (!inputText.trim()) {
      toast.error('Please select text or ensure the document has content');
      return;
    }

    if (!summaryPrompt || !summaryPrompt.trim()) {
      toast.error('Summary prompt is not available. Please wait a moment and try again.');
      // Try to fetch it again
      try {
        await fetchSummaryPrompt();
        const summariesData = await toneShiftApi.getSummaries();
        const defaultSummary = summariesData.find(s => s.name === 'Summarize Text');
        if (defaultSummary) {
          setSummaryPrompt(defaultSummary.updated_prompt || defaultSummary.starter_prompt || '');
        } else {
          return;
        }
      } catch (error) {
        console.error('Error fetching summary prompt:', error);
        return;
      }
    }

    if (!selectedModel) {
      toast.error('Please select a model');
      return;
    }

    setIsSummarizing(true);
    setShowSummaryModal(true);
    setSummaryResponse('');

    try {
      const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
      
      // Build the complete prompt by combining the summary prompt with the text
      const completePrompt = `${summaryPrompt}\n\nText to summarize:\n${inputText}`;

      // Call LLM service
      const result = await llmService.generateText({
        prompt: completePrompt,
        provider: selectedModelInfo?.provider,
        parameters: selectedModelInfo?.model ? { 
          model: selectedModelInfo.model,
          ...(selectedModelInfo?.provider === 'OpenAI' && { temperature: 0.7, max_tokens: 2000 })
        } : {}
      });

      if (result.success && result.data) {
        let responseText = result.data.response_text || result.data.response || result.data.content || '';
        
        // Strip markdown code blocks if present
        responseText = responseText.trim();
        if (responseText.startsWith('```json')) {
          responseText = responseText.replace(/^```json\s*/i, '').replace(/```\s*$/, '');
        } else if (responseText.startsWith('```')) {
          responseText = responseText.replace(/^```\s*/, '').replace(/```\s*$/, '');
        }
        responseText = responseText.trim();
        
        // Try to parse JSON response
        try {
          const jsonResponse = JSON.parse(responseText);
          setSummaryResponse(jsonResponse);
          // Add to history
          setSummaryHistory(prev => [{
            id: Date.now(),
            timestamp: new Date().toISOString(),
            response: jsonResponse,
            text: inputText,
            type: 'summary'
          }, ...prev]);
        } catch (e) {
          // If not JSON, store as raw text
          setSummaryResponse(responseText);
          // Add to history
          setSummaryHistory(prev => [{
            id: Date.now(),
            timestamp: new Date().toISOString(),
            response: responseText,
            text: inputText,
            type: 'summary'
          }, ...prev]);
        }
      } else {
        throw new Error(result.error || 'Failed to generate summary');
      }
    } catch (error) {
      console.error('Error summarizing text:', error);
      toast.error(error.message || 'Failed to summarize text');
      setShowSummaryModal(false);
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleSummaryDiscard = () => {
    setShowSummaryModal(false);
    setSummaryResponse('');
    setIsSummarizing(false);
  };

  // Handle chat edit - continue conversation
  const handleChatEdit = () => {
    setShowChatEdit(true);
  };

  // Handle chat edit submit
  const handleChatEditSubmit = async () => {
    if (!chatEditInput.trim() || !currentConversationId) return;

    const editText = chatEditInput.trim();
    setChatEditInput('');
    setIsGenerating(true);

    try {
      const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
      
      // Build prompt with conversation history (previous message, response, and new edit)
      const firstUserMessage = conversationMessages.find(m => m.type === 'input');
      const lastAiMessage = conversationMessages.filter(m => m.type === 'output').slice(-1)[0];
      
      const historyText = firstUserMessage && lastAiMessage 
        ? `Previous conversation:\nUser: ${firstUserMessage.text}\nAssistant: ${lastAiMessage.text}\n\nNow make these changes: ${editText}`
        : editText;

      // Build the prompt
      const { text: completePrompt } = buildToneShiftPrompt({
        inputText: historyText,
        messages: conversationMessages,
        voices: toneShiftData.voices,
        roles: toneShiftData.roles,
        formats: toneShiftData.formats,
        audiences: toneShiftData.audiences,
        modifiers: toneShiftData.modifiers,
        selectedVoice,
        selectedRole,
        selectedFormat,
        selectedAudiences,
        modifierLevelsMap: activeModifierLevels(modifierLevels),
        selectedModelInfo,
        getModifierLevelName: (name, level) => {
          const modifier = toneShiftData.modifiers.find(m => m.name === name);
          if (!modifier) return null;
          const levelName = getModifierLevelNameFromMapping(name, level);
          return levelName;
        },
        getLevelDisplayName
      });

      const modelInfo = selectedModelInfo ? { provider: selectedModelInfo.provider, model: selectedModelInfo.model } : null;
      
      const messageResult = await toneShiftApi.sendMessage(currentConversationId, {
        content: completePrompt,
        message_type: 'additional_instructions',
        provider: modelInfo ? modelInfo.provider : null,
        model: modelInfo ? modelInfo.model : null
      });

      if (messageResult.ai_response) {
        const responseText = messageResult.ai_response.content;
        const additionalContextText = messageResult.ai_response.metadata?.additional_context || '';
        setGeneratedResponse(responseText);
        setAdditionalContext(additionalContextText);
        
        // Add to conversation messages
        const userMessage = {
          id: Date.now(),
          type: 'input',
          text: editText,
          timestamp: new Date().toLocaleTimeString()
        };
        const aiMessage = {
          id: Date.now() + 1,
          type: 'output',
          text: responseText,
          additionalContext: additionalContextText,
          timestamp: new Date().toLocaleTimeString()
        };
        setConversationMessages(prev => [...prev, userMessage, aiMessage]);
      } else {
        throw new Error(messageResult.error || 'Failed to get AI response');
      }
    } catch (error) {
      console.error('Error in chat edit:', error);
      toast.error(error.message || 'Failed to apply changes');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle edit save
  const handleEditSave = async (updatedItem) => {
    try {
      const data = {
        name: updatedItem.name,
        description: updatedItem.description,
        updated_prompt: updatedItem.prompt || updatedItem.updated_prompt
      };

      switch (editingItem?.type) {
        case 'voice':
          await toneShiftApi.updateVoice(editingItem.id, data);
          break;
        case 'role':
          await toneShiftApi.updateRole(editingItem.id, data);
          break;
        case 'format':
          await toneShiftApi.updateFormat(editingItem.id, data);
          break;
        case 'audience':
          await toneShiftApi.updateAudience(editingItem.id, data);
          break;
        case 'modifier':
          // For modifiers, update each level individually
          if (updatedItem.levels) {
            await Promise.all(
              updatedItem.levels.map(level =>
                toneShiftApi.updateModifierLevel(level.id, {
                  updated_prompt: level.updated_prompt || '',
                  level_label: level.level_label || ''
                })
              )
            );
          }
          // Update modifier itself
          await toneShiftApi.updateModifier(editingItem.id, {
            name: updatedItem.name,
            description: updatedItem.description,
            left: updatedItem.left,
            right: updatedItem.right
          });
          break;
        case 'analysis':
          // For analyses, we need to create or update via the API
          if (editingItem.id) {
            // Update existing analysis
            await toneShiftApi.updateAnalysis(editingItem.id, data);
          } else {
            // Create new analysis customization
            await toneShiftApi.createAnalysis(data);
          }
          break;
        case 'summary':
          // For summaries, we need to create or update via the API
          if (editingItem.id) {
            // Update existing summary
            await toneShiftApi.updateSummary(editingItem.id, data);
          } else {
            // Create new summary customization
            await toneShiftApi.createSummary(data);
          }
          break;
        default:
          console.warn('Unknown item type:', editingItem?.type);
      }

      // Refetch data to update the UI
      if (editingItem?.type === 'analysis') {
        await fetchAnalyses();
      } else {
        await fetchToneShiftData();
      }
      setShowEditModal(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  return (
    <div className="document-editor" onKeyDown={handleKeyDown}>
      {/* Level 1: File Management Header */}
      <div className="document-header">
        <div className="document-logo">C</div>
        <div className="document-title-section">
          <input
            type="text"
            className="document-title-input"
            value={currentDocument?.title || ''}
            onChange={handleTitleChange}
            placeholder="Untitled document"
          />
          <div className="document-status">
            <span className={isSaving ? 'saving' : ''}>
              {isSaving ? 'Saving...' : hasUnsavedChanges ? 'Unsaved changes' : currentDocument?.id ? 'Saved' : 'New document'}
            </span>
          </div>
        </div>
      </div>

      {/* Level 2: Function Selection */}
      <div className="document-function-bar">
        <button 
          className={`function-btn ${activeFunction === 'manage-file' ? 'active' : ''}`}
          onClick={() => setActiveFunction('manage-file')}
        >
          Manage File
        </button>
        <button 
          className={`function-btn ${activeFunction === 'format-text' ? 'active' : ''}`}
          onClick={() => setActiveFunction('format-text')}
        >
          Format Text
        </button>
        <button 
          className={`function-btn ${activeFunction === 'transform' ? 'active' : ''}`}
          onClick={() => setActiveFunction('transform')}
        >
          Transform
        </button>
        <button 
          className={`function-btn ${activeFunction === 'analyze' ? 'active' : ''}`}
          onClick={() => setActiveFunction('analyze')}
        >
          Analyze
        </button>
      </div>

      {/* Level 3: Function-Specific Toolbar */}
      {showFunctionToolbar && (
        <div className="document-toolbar">
          {activeFunction === 'manage-file' && (
            <>
              <button 
                className="document-toolbar-btn document-save-btn" 
                onClick={saveDocument}
                disabled={isSaving}
                title="Save document (Ctrl+S)"
              >
                <Save size={16} style={{marginRight: '6px'}} />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              
              <button 
                className="document-toolbar-btn" 
                onClick={handleNewDocument}
                title="Create new document"
              >
                <FilePlus size={16} style={{marginRight: '6px'}} />
                New
              </button>
              
              <button 
                className="document-toolbar-btn" 
                onClick={handleOpenDocument}
                title="Open document"
              >
                <FolderOpen size={16} style={{marginRight: '6px'}} />
                Open
              </button>
              
              {currentDocument?.id && (
                <button 
                  className="document-toolbar-btn document-delete-btn" 
                  onClick={handleDeleteDocument}
                  title="Delete current document"
                >
                  <Trash2 size={16} style={{marginRight: '6px'}} />
                  Delete
                </button>
              )}
              
              <div className="document-toolbar-separator"></div>
              
              <button 
                className="document-toolbar-btn" 
                onClick={toggleWordCount}
                title="Toggle word count"
              >
                <BarChart3 size={16} style={{marginRight: '6px'}} />
                Word Count
              </button>
              
              <button 
                className="document-toolbar-btn" 
                onClick={() => window.print()}
                title="Print document"
              >
                <Printer size={16} style={{marginRight: '6px'}} />
                Print
              </button>
              
              <button 
                className="document-toolbar-btn" 
                onClick={toggleHelp}
                title="Show help"
              >
                <HelpCircle size={16} style={{marginRight: '6px'}} />
                Help
              </button>
            </>
          )}
          
          {activeFunction === 'format-text' && (
            <>
              <button 
                className="document-toolbar-btn" 
                onClick={() => formatDoc('undo')}
                title="Undo"
              >
                ↶
              </button>
              <button 
                className="document-toolbar-btn" 
                onClick={() => formatDoc('redo')}
                title="Redo"
              >
                ↷
              </button>
              
              <div className="document-toolbar-separator"></div>
              
              <select 
                className="document-toolbar-dropdown" 
                onChange={(e) => formatDoc('fontName', e.target.value)}
              >
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Courier New">Courier New</option>
                <option value="Georgia">Georgia</option>
                <option value="Verdana">Verdana</option>
                <option value="Comic Sans MS">Comic Sans MS</option>
                <option value="Roboto">Roboto</option>
              </select>
              
              <select 
                className="document-toolbar-dropdown" 
                style={{width: '60px'}}
                onChange={(e) => formatDoc('fontSize', e.target.value)}
                defaultValue="3"
              >
                <option value="1">8</option>
                <option value="2">10</option>
                <option value="3">12</option>
                <option value="4">14</option>
                <option value="5">18</option>
                <option value="6">24</option>
                <option value="7">36</option>
              </select>
              
              <div className="document-toolbar-separator"></div>
              
              <button 
                className={`document-toolbar-btn ${isCommandActive('bold') ? 'active' : ''}`}
                onClick={() => formatDoc('bold')}
                title="Bold (Ctrl+B)"
              >
                B
              </button>
              <button 
                className={`document-toolbar-btn ${isCommandActive('italic') ? 'active' : ''}`}
                onClick={() => formatDoc('italic')}
                title="Italic (Ctrl+I)"
                style={{fontStyle: 'italic'}}
              >
                I
              </button>
              <button 
                className={`document-toolbar-btn ${isCommandActive('underline') ? 'active' : ''}`}
                onClick={() => formatDoc('underline')}
                title="Underline (Ctrl+U)"
                style={{textDecoration: 'underline'}}
              >
                U
              </button>
              <button 
                className={`document-toolbar-btn ${isCommandActive('strikeThrough') ? 'active' : ''}`}
                onClick={() => formatDoc('strikeThrough')}
                title="Strikethrough"
                style={{textDecoration: 'line-through'}}
              >
                S
              </button>
              
              <div className="document-toolbar-separator"></div>
              
              <select 
                className="document-toolbar-dropdown" 
                style={{width: '100px'}}
                value={lineSpacing}
                onChange={(e) => changeLineSpacing(e.target.value)}
              >
                <option value="1">Single</option>
                <option value="1.15">1.15</option>
                <option value="1.5">1.5</option>
                <option value="2">Double</option>
              </select>
              
              <div className="document-toolbar-separator"></div>
              
              <button 
                className={`document-toolbar-btn ${isCommandActive('insertUnorderedList') ? 'active' : ''}`}
                onClick={() => formatDoc('insertUnorderedList')}
                title="Bullet list"
              >
                •
              </button>
              <button 
                className={`document-toolbar-btn ${isCommandActive('insertOrderedList') ? 'active' : ''}`}
                onClick={() => formatDoc('insertOrderedList')}
                title="Numbered list"
              >
                1.
              </button>
              
              <button 
                className="document-toolbar-btn"
                onClick={() => formatDoc('outdent')}
                title="Decrease indent"
              >
                ⇤
              </button>
              <button 
                className="document-toolbar-btn"
                onClick={() => formatDoc('indent')}
                title="Increase indent"
              >
                ⇥
              </button>
              
              <div className="document-toolbar-separator"></div>
              
              <select 
                className="document-toolbar-dropdown" 
                style={{width: '100px'}}
                onChange={(e) => formatDoc('formatBlock', e.target.value)}
              >
                <option value="p">Normal</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
                <option value="blockquote">Quote</option>
              </select>
            </>
          )}
          
          {activeFunction === 'transform' && (
            <>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <Selector
                  title="Format"
                  icon={{ png: '/images/medium.png', gif: '/images/medium.gif' }}
                  items={toneShiftData.formats}
                  selected={selectedFormat}
                  onSelect={(id) => setSelectedFormat(id)}
                  onEdit={(item) => handleEditItem(item)}
                  disabled={loading}
                />

                <Selector
                  title="Audience"
                  icon={{ png: '/images/audience.png', gif: '/images/audience.gif' }}
                  items={toneShiftData.audiences}
                  selected={selectedAudiences}
                  multi
                  onSelect={(id, checked) =>
                    setSelectedAudiences(prev => checked ? [...prev, id] : prev.filter(x => x !== id))
                  }
                  onEdit={(item) => handleEditItem(item)}
                  disabled={loading}
                />

                <Selector
                  title="Modifiers"
                  type="modifier"
                  icon={{ png: '/images/modifiers.png', gif: '/images/modifiers.gif' }}
                  items={toneShiftData.modifiers}
                  selected={modifierLevels}
                  multi={false}
                  showNone={false}
                  onSelect={() => {}} // Not used for modifiers
                  onEdit={(item) => handleEditItem(item)}
                  modifierLevels={modifierLevels}
                  onAdjust={adjustModifier}
                  getModifierLabels={getModifierLabels}
                  getLevelDisplayName={getLevelDisplayName}
                  disabled={loading}
                />

                <Selector
                  title="Voice"
                  icon={{ png: '/images/voice.png', gif: '/images/voice.gif' }}
                  items={toneShiftData.voices}
                  selected={selectedVoice}
                  onSelect={(id) => setSelectedVoice(id)}
                  onEdit={(item) => handleEditItem(item)}
                  disabled={loading}
                />

                <Selector
                  title="Role"
                  icon={{ png: '/images/role.png', gif: '/images/role.gif' }}
                  items={toneShiftData.roles}
                  selected={selectedRole}
                  onSelect={(id) => setSelectedRole(id)}
                  onEdit={(item) => handleEditItem(item)}
                  disabled={loading}
                />

                <Selector
                  title={(() => {
                    if (modelsLoading) return "Loading...";
                    if (!selectedModel || !availableModels.length) return "Model";
                    const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
                    return selectedModelInfo ? selectedModelInfo.display || selectedModelInfo.model : "Model";
                  })()}
                  icon={{ png: '/images/models.png', gif: '/images/models.gif' }}
                  items={(availableModels || []).map(m => ({
                    id: m.id,
                    name: m.display || m.model,
                    description: m.provider ? `${m.provider} • ${m.model}` : m.model,
                  }))}
                  selected={selectedModel}
                  onSelect={setSelectedModel}
                  multi={false}
                  showNone={false}
                  disabled={!!modelsLoading}
                />

                <div className="document-toolbar-separator"></div>

                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !selectedModel}
                  className="send-btn"
                  title="Transform"
                >
                  <img 
                    src="/images/generate.png" 
                    alt="Transform" 
                    className="send-icon"
                    onMouseEnter={(e) => e.target.src = "/images/generate.gif"}
                    onMouseLeave={(e) => e.target.src = "/images/generate.png"}
                  />
                </button>
              </div>
              
              {/* Selection Counter Bar */}
              {(() => {
                const stats = getSelectionStats();
                return (
                  <div className="transform-selection-counter">
                    {stats.selected} of {stats.total} {stats.total === 1 ? 'character' : 'characters'} selected
                    {stats.selected === stats.total && stats.total > 0 && ' (entire document)'}
                  </div>
                );
              })()}
            </>
          )}
          
          {activeFunction === 'analyze' && (
            <>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <Selector
                  title={(() => {
                    if (modelsLoading) return "Loading...";
                    if (!selectedModel || !availableModels.length) return "Model";
                    const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
                    return selectedModelInfo ? selectedModelInfo.display || selectedModelInfo.model : "Model";
                  })()}
                  icon={{ png: '/images/models.png', gif: '/images/models.gif' }}
                  items={(availableModels || []).map(m => ({
                    id: m.id,
                    name: m.display || m.model,
                    description: m.provider ? `${m.provider} • ${m.model}` : m.model,
                  }))}
                  selected={selectedModel}
                  onSelect={setSelectedModel}
                  multi={false}
                  showNone={false}
                  disabled={!!modelsLoading}
                />

                <div className="document-toolbar-separator"></div>

                {(analysisHistory.length > 0 || summaryHistory.length > 0) && (
                  <div style={{ position: 'relative' }} ref={historyDropdownRef}>
                    <button
                      onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                      className="document-toolbar-btn"
                      title="View history"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        position: 'relative'
                      }}
                    >
                      <Clock size={16} />
                      <span style={{ fontSize: '0.875rem' }}>History</span>
                      <ChevronDown 
                        size={14} 
                        style={{ 
                          transition: 'transform 0.2s',
                          transform: showHistoryDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                        }} 
                      />
                    </button>
                    
                    {showHistoryDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '0.5rem',
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        zIndex: 1000,
                        minWidth: '400px',
                        maxWidth: '520px',
                        maxHeight: '500px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        {/* Header */}
                        <div style={{
                          padding: '1.5rem 1.5rem 1rem 1.5rem',
                          borderBottom: '1px solid #e5e7eb',
                          backgroundColor: 'white'
                        }}>
                          <h3 style={{
                            margin: '0 0 0.5rem 0',
                            fontSize: '1.25rem',
                            fontWeight: '600',
                            color: '#1f2937'
                          }}>
                            Recent History
                          </h3>
                          <p style={{
                            margin: 0,
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            lineHeight: '1.5'
                          }}>
                            {analysisHistory.length + summaryHistory.length} {analysisHistory.length + summaryHistory.length === 1 ? 'item' : 'items'} available
                          </p>
                        </div>

                        {/* Scrollable content */}
                        <div style={{
                          overflowY: 'auto',
                          maxHeight: '400px',
                          padding: '0.5rem 0'
                        }}>
                          {(() => {
                            // Combine and sort histories by timestamp (most recent first)
                            const combinedHistory = [
                              ...analysisHistory.map(h => ({ ...h, type: 'analysis' })),
                              ...summaryHistory.map(h => ({ ...h, type: 'summary' }))
                            ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

                            if (combinedHistory.length === 0) {
                              return (
                                <div style={{
                                  padding: '3rem 1.5rem',
                                  textAlign: 'center',
                                  color: '#6b7280',
                                  fontSize: '0.875rem'
                                }}>
                                  No history available
                                </div>
                              );
                            }

                            return (
                              <div style={{ padding: '0 0.5rem' }}>
                                {combinedHistory.map((item, index) => {
                                  const date = new Date(item.timestamp);
                                  const isToday = date.toDateString() === new Date().toDateString();
                                  const dateStr = isToday 
                                    ? 'Today' 
                                    : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                                  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                  const previewText = item.text.length > 80 
                                    ? item.text.substring(0, 80) + '...' 
                                    : item.text;

                                  return (
                                    <button
                                      key={item.id}
                                      onClick={() => {
                                        if (item.type === 'analysis') {
                                          setAnalysisResponse(item.response);
                                          setShowAnalysisModal(true);
                                          setShowHistoryDropdown(false);
                                          toast.success('Analysis loaded');
                                        } else {
                                          setSummaryResponse(item.response);
                                          setShowSummaryModal(true);
                                          setShowHistoryDropdown(false);
                                          toast.success('Summary loaded');
                                        }
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '1.25rem 1.5rem',
                                        marginBottom: index < combinedHistory.length - 1 ? '0.5rem' : '0',
                                        border: 'none',
                                        borderRadius: '8px',
                                        backgroundColor: 'transparent',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.75rem',
                                        position: 'relative'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = item.type === 'analysis' ? '#eff6ff' : '#f0fdf4';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'transparent';
                                      }}
                                    >
                                      {/* Type badge and timestamp row */}
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: '1rem'
                                      }}>
                                        <div style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '0.5rem',
                                          padding: '0.375rem 0.75rem',
                                          borderRadius: '6px',
                                          backgroundColor: item.type === 'analysis' ? '#eff6ff' : '#f0fdf4',
                                          fontSize: '0.75rem',
                                          fontWeight: '600',
                                          color: item.type === 'analysis' ? '#1e40af' : '#166534',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.025em',
                                          border: `1px solid ${item.type === 'analysis' ? '#bfdbfe' : '#86efac'}`
                                        }}>
                                          <span style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: item.type === 'analysis' ? '#3b82f6' : '#10b981'
                                          }} />
                                          {item.type === 'analysis' ? 'Analysis' : 'Summary'}
                                        </div>
                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.5rem',
                                          fontSize: '0.8125rem',
                                          color: '#6b7280',
                                          fontWeight: '500'
                                        }}>
                                          <span>{dateStr}</span>
                                          <span style={{ color: '#d1d5db' }}>•</span>
                                          <span>{timeStr}</span>
                                        </div>
                                      </div>

                                      {/* Preview text */}
                                      <div style={{
                                        fontSize: '0.9375rem',
                                        color: '#374151',
                                        lineHeight: '1.6',
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        textOverflow: 'ellipsis',
                                        paddingLeft: '0.25rem'
                                      }}>
                                        {previewText}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing || !selectedModel || !analysisPrompt}
                  className="send-btn"
                  title="Analyze"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.5rem 1rem',
                    width: 'auto',
                    minWidth: 'auto',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    const img = e.currentTarget.querySelector('img');
                    if (img) img.src = "/images/analyze.gif";
                  }}
                  onMouseLeave={(e) => {
                    const img = e.currentTarget.querySelector('img');
                    if (img) img.src = "/images/analyze.png";
                  }}
                >
                  <img 
                    src="/images/analyze.png" 
                    alt="Analyze" 
                    className="send-icon"
                    style={{ width: '1.5rem', height: '1.5rem', objectFit: 'contain' }}
                  />
                  <span style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Analyze</span>
                </button>

                <button
                  onClick={handleSummarize}
                  disabled={isSummarizing || !selectedModel || !summaryPrompt}
                  className="send-btn"
                  title="Summarize"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.5rem 1rem',
                    width: 'auto',
                    minWidth: 'auto',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    const img = e.currentTarget.querySelector('img');
                    if (img) img.src = "/images/summarize.gif";
                  }}
                  onMouseLeave={(e) => {
                    const img = e.currentTarget.querySelector('img');
                    if (img) img.src = "/images/summarize.png";
                  }}
                >
                  <img 
                    src="/images/summarize.png" 
                    alt="Summarize" 
                    className="send-icon"
                    style={{ width: '1.5rem', height: '1.5rem', objectFit: 'contain' }}
                  />
                  <span style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Summarize</span>
                </button>
              </div>
              
              {/* Selection Counter Bar */}
              {(() => {
                const stats = getSelectionStats();
                return (
                  <div className="transform-selection-counter">
                    {stats.selected} of {stats.total} {stats.total === 1 ? 'character' : 'characters'} selected
                    {stats.selected === stats.total && stats.total > 0 && ' (entire document)'}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* Editor */}
      <div className="document-editor-container">
        <div className="document-page-wrapper">
          <div
            ref={editorRef}
            className="document-editor-content"
            contentEditable={true}
            onInput={handleEditorInput}
            onPaste={handlePaste}
            style={{lineHeight: lineSpacing}}
          />
        </div>
      </div>

      {/* Word Count Display */}
      <div className={`document-word-count ${wordCountVisible ? 'visible' : ''}`}>
        Words: {wordCount.words} | Characters: {wordCount.characters}
      </div>

      {/* Help Panel */}
      <div className={`document-info-panel ${helpPanelVisible ? 'visible' : ''}`}>
        <div className="document-info-title">📚 Quick Help</div>
        <div className="document-info-content">
          <strong>Keyboard Shortcuts:</strong><br/>
          • Ctrl+B: Bold<br/>
          • Ctrl+I: Italic<br/>
          • Ctrl+U: Underline<br/>
          • Ctrl+S: Save<br/>
          • Ctrl+P: Print<br/>
          • Ctrl+Z: Undo<br/>
          • Ctrl+Y: Redo<br/><br/>
          <strong>Tips:</strong><br/>
          • Select text to apply formatting<br/>
          • Use heading styles for structure<br/>
          • Lists automatically format as you type
        </div>
      </div>

      {/* Document Picker Modal */}
      <DocumentPickerModal
        isOpen={showDocumentPicker}
        onClose={() => setShowDocumentPicker(false)}
        onSelectDocument={handleSelectDocument}
      />

      {/* Edit Modal */}
      <EditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingItem(null);
        }}
        editingItem={editingItem}
        onSave={handleEditSave}
        isLoading={false}
      />

      {/* Transform Response Modal - Side Modal like BaseContentForm */}
      <div 
        className={`overlay ${showResponseModal ? 'open' : ''}`}
        onClick={handleDiscard}
      />
      <div className={`slide-in-form transform-response-modal ${showResponseModal ? 'open' : ''}`}>
        <div className="form-header">
          <h2>Transform Result</h2>
          <div className="form-controls">
            <button className="close-button" onClick={handleDiscard}>
              ×
            </button>
          </div>
        </div>
        <div className="transform-response-content">
        {isGenerating ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', color: '#666', marginBottom: '1rem' }}>Generating transform...</div>
            <div style={{ fontSize: '0.9rem', color: '#999' }}>Please wait</div>
          </div>
        ) : generatedResponse ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {additionalContext && (
              <div style={{ 
                marginBottom: '1rem',
                padding: '1rem',
                fontSize: '0.95rem',
                lineHeight: '1.6',
                color: '#1f2937',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}>
                {additionalContext}
              </div>
            )}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              marginBottom: '1rem',
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '0.5rem 0.75rem',
                backgroundColor: 'rgba(243, 244, 246, 0.8)',
                borderBottom: '1px solid #e5e7eb',
                borderRadius: '8px 8px 0 0'
              }}>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedResponse);
                    toast.success('Response copied to clipboard');
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280',
                    borderRadius: '4px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                    e.target.style.color = '#1f2937';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6b7280';
                  }}
                  title="Copy response"
                >
                  <Copy size={16} />
                </button>
              </div>
              <div style={{ 
                flex: 1,
                padding: '1rem',
                backgroundColor: 'rgba(249, 250, 251, 0.8)',
                border: '1px solid #e5e7eb',
                borderRadius: '0 0 8px 8px',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowY: 'auto'
              }}>
                {generatedResponse}
              </div>
            </div>
            
            {showChatEdit && (
              <div style={{ 
                marginBottom: '1rem',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: 'white'
              }}>
                <textarea
                  value={chatEditInput}
                  onChange={(e) => setChatEditInput(e.target.value)}
                  placeholder="Describe the changes you want to make..."
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    resize: 'vertical'
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleChatEditSubmit();
                    }
                  }}
                />
                <button
                  onClick={handleChatEditSubmit}
                  disabled={!chatEditInput.trim() || isGenerating}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: '#1a73e8',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    opacity: (!chatEditInput.trim() || isGenerating) ? 0.5 : 1
                  }}
                >
                  {isGenerating ? 'Applying...' : 'Apply Changes'}
                </button>
              </div>
            )}
            
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              paddingTop: '1rem',
              borderTop: '1px solid #eee'
            }}>
              <button
                onClick={handleDiscard}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}
              >
                Discard
              </button>
              <button
                onClick={handleCommit}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#1a73e8',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}
              >
                Commit
              </button>
              <button
                onClick={handleChatEdit}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '1px solid #1a73e8',
                  borderRadius: '8px',
                  background: showChatEdit ? '#e8f0fe' : 'white',
                  color: '#1a73e8',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}
              >
                {showChatEdit ? 'Hide Edit' : 'Edit'}
              </button>
            </div>
          </div>
        ) : null}
        </div>
      </div>

      {/* Summary Response Modal */}
      {showSummaryModal && (
        <>
          <div 
            className={`overlay ${showSummaryModal ? 'open' : ''}`}
            onClick={handleSummaryDiscard}
          />
          <div className={`slide-in-form transform-response-modal ${showSummaryModal ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <h2>Summary Result</h2>
              <div className="form-controls">
                <button className="close-button" onClick={handleSummaryDiscard}>
                  ×
                </button>
              </div>
            </div>
            <div className="transform-response-content">
            {isSummarizing ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', color: '#666', marginBottom: '1rem' }}>Summarizing text...</div>
                <div style={{ fontSize: '0.9rem', color: '#999' }}>Please wait</div>
              </div>
            ) : summaryResponse ? (
              (() => {
                const isJson = typeof summaryResponse === 'object' && summaryResponse !== null;
                const summary = isJson ? summaryResponse.Summary : null;
                const keyPoints = isJson ? summaryResponse.Key_Points : null;
                const context = isJson ? summaryResponse.Context : null;
                const styleNotes = isJson ? summaryResponse.Style_Notes : null;
                const suggestedImprovements = isJson ? summaryResponse.Suggested_Improvements : null;
                const suggestedResponses = isJson ? summaryResponse.Suggested_Responses : null;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '1.5rem', overflowY: 'auto' }}>
                    {/* Summary Section */}
                    {summary && (
                      <div style={{
                        padding: '1.5rem',
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}>
                        <h3 style={{ 
                          margin: '0 0 1rem 0', 
                          fontSize: '1.25rem', 
                          fontWeight: '600',
                          color: '#1f2937'
                        }}>
                          Summary
                        </h3>
                        <p style={{
                          margin: 0,
                          fontSize: '0.95rem',
                          lineHeight: '1.6',
                          color: '#374151',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {summary}
                        </p>
                      </div>
                    )}

                    {/* Key Points Section */}
                    {keyPoints && Array.isArray(keyPoints) && keyPoints.length > 0 && (
                      <div style={{
                        padding: '1.5rem',
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '8px',
                        borderLeft: '4px solid #3b82f6'
                      }}>
                        <h3 style={{ 
                          margin: '0 0 1rem 0', 
                          fontSize: '1.25rem', 
                          fontWeight: '600',
                          color: '#1e40af'
                        }}>
                          Key Points
                        </h3>
                        <ul style={{
                          margin: 0,
                          paddingLeft: '1.5rem',
                          listStyle: 'none'
                        }}>
                          {keyPoints.map((point, index) => (
                            <li key={index} style={{
                              marginBottom: '0.75rem',
                              fontSize: '0.95rem',
                              lineHeight: '1.6',
                              color: '#1e3a8a',
                              position: 'relative',
                              paddingLeft: '1.5rem'
                            }}>
                              <span style={{
                                position: 'absolute',
                                left: 0,
                                top: '0.5rem',
                                width: '6px',
                                height: '6px',
                                backgroundColor: '#3b82f6',
                                borderRadius: '50%'
                              }} />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Context Section */}
                    {context && context !== 'none' && (
                      <div style={{
                        padding: '1rem 1.5rem',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #fde047',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ 
                          margin: '0 0 0.5rem 0', 
                          fontSize: '1rem', 
                          fontWeight: '600',
                          color: '#92400e'
                        }}>
                          Context
                        </h4>
                        <p style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          lineHeight: '1.5',
                          color: '#78350f'
                        }}>
                          {context}
                        </p>
                      </div>
                    )}

                    {/* Style Notes Section */}
                    {styleNotes && styleNotes !== 'none' && (
                      <div style={{
                        padding: '1rem 1.5rem',
                        backgroundColor: '#f5f3ff',
                        border: '1px solid #c4b5fd',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ 
                          margin: '0 0 0.5rem 0', 
                          fontSize: '1rem', 
                          fontWeight: '600',
                          color: '#5b21b6'
                        }}>
                          Style Notes
                        </h4>
                        <p style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          lineHeight: '1.5',
                          color: '#6d28d9'
                        }}>
                          {styleNotes}
                        </p>
                      </div>
                    )}

                    {/* Suggested Improvements Section */}
                    {suggestedImprovements && Array.isArray(suggestedImprovements) && suggestedImprovements.length > 0 && (
                      <div style={{
                        padding: '1.5rem',
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #86efac',
                        borderRadius: '8px',
                        borderLeft: '4px solid #22c55e'
                      }}>
                        <h3 style={{ 
                          margin: '0 0 1rem 0', 
                          fontSize: '1.25rem', 
                          fontWeight: '600',
                          color: '#166534'
                        }}>
                          Suggested Improvements
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          {suggestedImprovements.map((improvement, index) => (
                            <div key={index} style={{
                              position: 'relative',
                              padding: '1rem',
                              backgroundColor: 'white',
                              border: '1px solid #bbf7d0',
                              borderRadius: '6px',
                              fontSize: '0.95rem',
                              lineHeight: '1.6',
                              color: '#14532d'
                            }}>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(improvement);
                                  toast.success('Improvement copied to clipboard');
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '0.5rem',
                                  right: '0.5rem',
                                  background: 'transparent',
                                  border: '1px solid #bbf7d0',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  padding: '0.375rem 0.5rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  color: '#166534',
                                  fontSize: '0.75rem',
                                  transition: 'all 0.2s',
                                  opacity: 0.7
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.opacity = '1';
                                  e.target.style.backgroundColor = '#f0fdf4';
                                  e.target.style.borderColor = '#86efac';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.opacity = '0.7';
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.borderColor = '#bbf7d0';
                                }}
                                title="Copy improvement"
                              >
                                <Copy size={12} />
                              </button>
                              <div style={{ paddingRight: '2.5rem' }}>
                                {improvement}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Suggested Responses Section */}
                    {suggestedResponses && Array.isArray(suggestedResponses) && suggestedResponses.length > 0 && (
                      <div style={{
                        padding: '1.5rem',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        borderLeft: '4px solid #ef4444'
                      }}>
                        <h3 style={{ 
                          margin: '0 0 1rem 0', 
                          fontSize: '1.25rem', 
                          fontWeight: '600',
                          color: '#991b1b'
                        }}>
                          Suggested Responses
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {suggestedResponses.map((response, index) => (
                            <div key={index} style={{
                              position: 'relative',
                              padding: '0.75rem 1rem',
                              backgroundColor: 'white',
                              border: '1px solid #fca5a5',
                              borderRadius: '6px',
                              fontSize: '0.95rem',
                              lineHeight: '1.5',
                              color: '#7f1d1d'
                            }}>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(response);
                                  toast.success('Response copied to clipboard');
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '0.5rem',
                                  right: '0.5rem',
                                  background: 'transparent',
                                  border: '1px solid #fca5a5',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  padding: '0.375rem 0.5rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  color: '#991b1b',
                                  fontSize: '0.75rem',
                                  transition: 'all 0.2s',
                                  opacity: 0.7
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.opacity = '1';
                                  e.target.style.backgroundColor = '#fef2f2';
                                  e.target.style.borderColor = '#fecaca';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.opacity = '0.7';
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.borderColor = '#fca5a5';
                                }}
                                title="Copy response"
                              >
                                <Copy size={12} />
                              </button>
                              <div style={{ paddingRight: '2.5rem' }}>
                                {response}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fallback for non-JSON responses */}
                    {!isJson && (
                      <div style={{ 
                        padding: '1rem',
                        backgroundColor: 'rgba(249, 250, 251, 0.8)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                      }}>
                        {typeof summaryResponse === 'string' ? summaryResponse : JSON.stringify(summaryResponse, null, 2)}
                      </div>
                    )}

                    {/* Copy button */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      padding: '0.5rem 0',
                      borderTop: '1px solid #eee'
                    }}>
                      <button
                        onClick={() => {
                          const textToCopy = isJson 
                            ? JSON.stringify(summaryResponse, null, 2)
                            : (typeof summaryResponse === 'string' ? summaryResponse : String(summaryResponse));
                          navigator.clipboard.writeText(textToCopy);
                          toast.success('Summary copied to clipboard');
                        }}
                        style={{
                          background: 'transparent',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          padding: '0.5rem 1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: '#6b7280',
                          fontSize: '0.9rem',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                          e.target.style.color = '#1f2937';
                          e.target.style.borderColor = '#9ca3af';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#6b7280';
                          e.target.style.borderColor = '#ddd';
                        }}
                        title="Copy summary"
                      >
                        <Copy size={16} />
                        Copy
                      </button>
                    </div>
                  </div>
                );
              })()
            ) : null}
            </div>
          </div>
        </>
      )}

      {/* Analysis Response Modal - Side Modal like Transform */}
      <div 
        className={`overlay ${showAnalysisModal ? 'open' : ''}`}
        onClick={handleAnalysisDiscard}
      />
      <div className={`slide-in-form transform-response-modal ${showAnalysisModal ? 'open' : ''}`}>
        <div className="form-header">
          <h2>Analysis Result</h2>
          <div className="form-controls">
            <button className="close-button" onClick={handleAnalysisDiscard}>
              ×
            </button>
          </div>
        </div>
        <div className="transform-response-content">
        {isAnalyzing ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', color: '#666', marginBottom: '1rem' }}>Analyzing text...</div>
            <div style={{ fontSize: '0.9rem', color: '#999' }}>Please wait</div>
          </div>
        ) : analysisResponse ? (
          (() => {
            // Check if response is JSON object or string
            const isJson = typeof analysisResponse === 'object' && analysisResponse !== null;
            const scores = isJson ? analysisResponse.Scores || {} : null;
            const synthesis = isJson ? analysisResponse.Synthesis || '' : null;
            const suggestions = isJson ? analysisResponse.Suggestions || [] : null;

            // Helper function to get dimension labels
            const getDimensionLabels = (dimensionName) => {
              const labelMap = {
                'Assertiveness': { left: 'Most Collaborative', right: 'Most Decisive' },
                'Length': { left: 'Most Concise', right: 'Most Detailed' },
                'Directness': { left: 'Most Diplomatic', right: 'Most Blunt' },
                'Energy': { left: 'Most Calm', right: 'Most Excited' },
                'Formality': { left: 'Most Casual', right: 'Most Professional' },
                'Urgency': { left: 'Most Relaxed', right: 'Most Immediate' },
                'Humor': { left: 'Most Humorous', right: 'Most Serious' },
                'Positivity': { left: 'Most Negative', right: 'Most Positive' },
                'Warmth': { left: 'Most Cool', right: 'Most Warm' },
                'Confidence': { left: 'Most Hedged', right: 'Most Confident' },
                'Personalization': { left: 'Most Impersonal', right: 'Most Personal' },
                'Deference': { left: 'Most Deferential', right: 'Most Authoritative' },
                'Specificity': { left: 'Most Abstract', right: 'Most Concrete' }
              };
              return labelMap[dimensionName] || { left: 'Left', right: 'Right' };
            };

            // Helper function to map label to score (-3 to 3)
            const getScoreFromLabel = (label, dimensionName) => {
              if (!label || label === 'null' || label === 'balanced') return 0;
              
              const labelLower = label.toLowerCase().replace(/\s+/g, '_');
              
              // Map all possible labels to scores
              const labelToScore = {
                // Assertiveness
                'most_collaborative': -3, 'more_collaborative': -2, 'collaborative': -1,
                'decisive': 1, 'more_decisive': 2, 'most_decisive': 3,
                // Length
                'most_concise': -3, 'more_concise': -2, 'concise': -1,
                'detailed': 1, 'more_detailed': 2, 'most_detailed': 3,
                // Directness
                'most_diplomatic': -3, 'more_diplomatic': -2, 'diplomatic': -1,
                'blunt': 1, 'more_blunt': 2, 'most_blunt': 3,
                // Energy
                'most_calm': -3, 'more_calm': -2, 'calm': -1,
                'excited': 1, 'more_excited': 2, 'most_excited': 3,
                // Formality
                'most_casual': -3, 'more_casual': -2, 'casual': -1,
                'professional': 1, 'more_professional': 2, 'most_professional': 3,
                // Urgency
                'most_relaxed': -3, 'more_relaxed': -2, 'relaxed': -1,
                'immediate': 1, 'more_immediate': 2, 'most_immediate': 3,
                // Humor
                'most_humorous': -3, 'more_humorous': -2, 'humorous': -1,
                'serious': 1, 'more_serious': 2, 'most_serious': 3,
                // Positivity
                'most_negative': -3, 'more_negative': -2, 'negative': -1,
                'positive': 1, 'more_positive': 2, 'most_positive': 3,
                // Warmth
                'most_cool': -3, 'more_cool': -2, 'cool': -1,
                'warm': 1, 'more_warm': 2, 'most_warm': 3,
                // Confidence
                'most_hedged': -3, 'more_hedged': -2, 'hedged': -1,
                'confident': 1, 'more_confident': 2, 'most_confident': 3,
                // Personalization
                'most_impersonal': -3, 'more_impersonal': -2, 'impersonal': -1,
                'personal': 1, 'more_personal': 2, 'most_personal': 3,
                // Deference
                'most_deferential': -3, 'more_deferential': -2, 'deferential': -1,
                'authoritative': 1, 'more_authoritative': 2, 'most_authoritative': 3,
                // Specificity
                'most_abstract': -3, 'more_abstract': -2, 'abstract': -1,
                'concrete': 1, 'more_concrete': 2, 'most_concrete': 3
              };
              
              return labelToScore[labelLower] ?? null;
            };

            return (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '1rem', overflowY: 'auto' }}>
                {/* Synthesis Section */}
                {synthesis && (
                  <div style={{
                    padding: '1.5rem',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 1rem 0', 
                      fontSize: '1.25rem', 
                      fontWeight: '600',
                      color: '#1f2937'
                    }}>
                      Analysis
                    </h3>
                    <p style={{
                      margin: 0,
                      fontSize: '0.95rem',
                      lineHeight: '1.6',
                      color: '#374151',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {synthesis}
                    </p>
                  </div>
                )}

                {/* Suggestions Section */}
                {suggestions && Array.isArray(suggestions) && suggestions.length > 0 && (
                  <div style={{
                    padding: '1.5rem',
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '8px',
                    borderLeft: '4px solid #3b82f6'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 1rem 0', 
                      fontSize: '1.25rem', 
                      fontWeight: '600',
                      color: '#1e40af'
                    }}>
                      Suggestions
                    </h3>
                    <ul style={{
                      margin: 0,
                      paddingLeft: '1.5rem',
                      listStyle: 'none'
                    }}>
                      {suggestions.map((suggestion, index) => (
                        <li key={index} style={{
                          marginBottom: '0.75rem',
                          fontSize: '0.95rem',
                          lineHeight: '1.6',
                          color: '#1e3a8a',
                          position: 'relative',
                          paddingLeft: '1.5rem'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '0.5rem',
                            width: '6px',
                            height: '6px',
                            backgroundColor: '#3b82f6',
                            borderRadius: '50%'
                          }} />
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Scores Section */}
                {scores && Object.keys(scores).length > 0 && (
                  <div style={{
                    padding: '1.5rem',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 1.5rem 0', 
                      fontSize: '1.25rem', 
                      fontWeight: '600',
                      color: '#1f2937'
                    }}>
                      Scores
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      {Object.entries(scores).map(([dimension, data]) => {
                        let score = data?.score;
                        const label = data?.label || '';
                        const dimensionLabels = getDimensionLabels(dimension);
                        
                        // Try to get score from label if score is missing or seems wrong
                        if (score === null || score === undefined) {
                          score = getScoreFromLabel(label, dimension);
                        } else {
                          // Convert old 1-7 scale to new -3 to 3 scale if needed
                          if (score >= 1 && score <= 7) {
                            score = score - 4; // Convert 1-7 to -3 to 3 (1->-3, 4->0, 7->3)
                          }
                          // If we have a label, prefer deriving score from label for accuracy
                          if (label && label !== 'null' && label !== 'balanced') {
                            const labelScore = getScoreFromLabel(label, dimension);
                            if (labelScore !== null) {
                              score = labelScore;
                            }
                          }
                        }
                        
                        const isNA = score === null || score === undefined;
                        
                        // Convert -3 to 3 scale to percentage where 0 is at 50% (center)
                        // -3 = 0%, 0 = 50%, 3 = 100%
                        // Clamp score to valid range
                        const clampedScore = Math.max(-3, Math.min(3, score || 0));
                        const indicatorPosition = !isNA ? ((clampedScore + 3) / 6) * 100 : 50;
                        const centerPosition = 50; // 0 is at center
                        
                        // Format label to title case
                        const formatLabel = (label) => {
                          if (!label || label === 'null') return '';
                          return label.split('_').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ');
                        };
                        
                        return (
                          <div key={dimension} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div style={{ 
                              fontSize: '0.95rem', 
                              fontWeight: '500', 
                              color: '#374151',
                              marginBottom: '0.25rem'
                            }}>
                              {dimension}: {isNA ? 'N/A' : formatLabel(label)}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ 
                                fontSize: '0.8rem', 
                                color: '#6b7280',
                                minWidth: '100px',
                                textAlign: 'right'
                              }}>
                                {dimensionLabels.left}
                              </span>
                              <div style={{
                                flex: 1,
                                height: '8px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                overflow: 'visible',
                                position: 'relative'
                              }}>
                                {isNA ? (
                                  <div style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>N/A</span>
                                  </div>
                                ) : (
                                  <>
                                    {/* Center line at 50% (0 position) */}
                                    <div style={{
                                      position: 'absolute',
                                      left: '50%',
                                      top: '0',
                                      bottom: '0',
                                      width: '2px',
                                      backgroundColor: '#9ca3af',
                                      transform: 'translateX(-50%)',
                                      opacity: 0.5
                                    }} />
                                    {/* Fill from left to indicator position */}
                                    {clampedScore < 0 ? (
                                      // Negative: fill from center leftward
                                      <div style={{
                                        position: 'absolute',
                                        left: `${indicatorPosition}%`,
                                        right: '50%',
                                        top: '0',
                                        bottom: '0',
                                        backgroundColor: '#ef4444',
                                        borderRadius: '4px 0 0 4px',
                                        transition: 'all 0.3s ease'
                                      }} />
                                    ) : clampedScore > 0 ? (
                                      // Positive: fill from center rightward
                                      <div style={{
                                        position: 'absolute',
                                        left: '50%',
                                        right: `${100 - indicatorPosition}%`,
                                        top: '0',
                                        bottom: '0',
                                        backgroundColor: '#3b82f6',
                                        borderRadius: '0 4px 4px 0',
                                        transition: 'all 0.3s ease'
                                      }} />
                                    ) : (
                                      // Exactly 0: no fill
                                      null
                                    )}
                                    {/* Indicator dot */}
                                    <div style={{
                                      position: 'absolute',
                                      left: `${indicatorPosition}%`,
                                      top: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      width: '14px',
                                      height: '14px',
                                      backgroundColor: clampedScore < 0 ? '#dc2626' : clampedScore > 0 ? '#1e40af' : '#6b7280',
                                      borderRadius: '50%',
                                      border: '2px solid white',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                      zIndex: 2
                                    }} />
                                  </>
                                )}
                              </div>
                              <span style={{ 
                                fontSize: '0.8rem', 
                                color: '#6b7280',
                                minWidth: '100px',
                                textAlign: 'left'
                              }}>
                                {dimensionLabels.right}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Fallback for non-JSON responses */}
                {!isJson && (
                  <div style={{ 
                    padding: '1rem',
                    backgroundColor: 'rgba(249, 250, 251, 0.8)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem'
                  }}>
                    {typeof analysisResponse === 'string' ? analysisResponse : JSON.stringify(analysisResponse, null, 2)}
                  </div>
                )}

                {/* Copy button */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: '0.5rem 0',
                  borderTop: '1px solid #eee'
                }}>
                  <button
                    onClick={() => {
                      const textToCopy = isJson 
                        ? JSON.stringify(analysisResponse, null, 2)
                        : (typeof analysisResponse === 'string' ? analysisResponse : String(analysisResponse));
                      navigator.clipboard.writeText(textToCopy);
                      toast.success('Analysis copied to clipboard');
                    }}
                    style={{
                      background: 'transparent',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      padding: '0.5rem 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#6b7280',
                      fontSize: '0.9rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                      e.target.style.color = '#1f2937';
                      e.target.style.borderColor = '#9ca3af';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#6b7280';
                      e.target.style.borderColor = '#ddd';
                    }}
                    title="Copy analysis"
                  >
                    <Copy size={16} />
                    Copy
                  </button>
                </div>

                {/* Close button */}
                <div style={{ 
                  display: 'flex', 
                  gap: '0.5rem', 
                  paddingTop: '0.5rem',
                  borderTop: '1px solid #eee'
                }}>
                  <button
                    onClick={handleAnalysisDiscard}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      background: 'white',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '500'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            );
          })()
        ) : null}
        </div>
      </div>
    </div>
  );
};

export default DocumentEditor;